import React, { useState, useEffect, useRef } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import Switch from 'react-switch';
import _ from 'lodash';

import './style.css';
import callApi from '../../api/callApi';
import { DataExtractionInput } from './data';
import Batch from './batch';


const DataExtraction = () => {
    const data = _.cloneDeep(DataExtractionInput);
    const [input, setInput] = useState({});
    const [output, setOutput] = useState({});
    const [error, setError] = useState(null);
    const fileInputRef = useRef(null);
    const [enabledAPIs, setEnabledAPIs] = useState([]);
    const [uploadedFiles, setUploadedFiles] = useState([]);

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => {
                resolve(fileReader.result);
            };
            fileReader.onerror = (error) => {
                reject(error);
            };
        });
    };

    const handleMultipleFiles = async (e, setFieldValue, setFieldError, fieldName) => {
        const files = Array.from(e.target.files);
        
        if (files.length === 0) {
            return;
        }

        // Clear any previous errors
        setFieldError(fieldName, '');

        try {
            const processedFiles = [];
            for (const file of files) {
                const base64 = await convertToBase64(file);
                const base64String = base64.split(',')[1];
                processedFiles.push({
                    file_name: file.name,
                    pdf_data: base64String,
                    password: '' // Default empty password, user can modify later
                });
            }
            
            setUploadedFiles(processedFiles);
            setFieldValue(fieldName, processedFiles);
            setOutput({}); // Clear previous output when new files are uploaded
            setError(null); // Clear previous error when new files are uploaded
        } catch (error) {
            console.error('Error converting files to base64:', error);
            setFieldError(fieldName, 'Error converting PDF files to base64!');
            setUploadedFiles([]);
            setFieldValue(fieldName, []);
            // Clear the file input on error
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const updateFilePassword = (index, password, setFieldValue, values, fieldName) => {
        // Use Formik's current values as the source of truth
        const updatedFiles = [...values.files];
        updatedFiles[index].password = password;
        
        // Update both Formik state and local state
        setFieldValue(fieldName, updatedFiles);
        setUploadedFiles(updatedFiles);
    };

    const removeFile = (index, setFieldValue, values) => {
        const updatedFiles = values.files.filter((_, i) => i !== index);
        setFieldValue('files', updatedFiles);
        setUploadedFiles(updatedFiles);
        
        // If no files left, clear the file input
        if (updatedFiles.length === 0 && fileInputRef.current) {
            fileInputRef.current.value = '';
        }
    };

    const handleSubmit = (inputData, setSubmitting) => {
        setInput(inputData);
        setOutput({});
        setError(null);
        
        let url = `${process.env.REACT_APP_API_URL}/v1/portal/extract_statement/batch`;
        callApi(url, 'POST', inputData)
            .then((json) => {
                if (json.data) {
                    setOutput(json.data);
                }
                else if (json.error) {
                    setError(json.error);
                } 
                setSubmitting(false);
            })
            .catch((error) => {
                console.error('Error:', error);
                setError('Failed to extract data. Please try again.');
                setSubmitting(false);
            });
    };

    const getEnabledAPIs = () => {
        let url = `${process.env.REACT_APP_API_URL}/v1/portal/enabled_apis`;
        callApi(url, 'GET')
            .then((json) => {
                if (json.data) {
                    setEnabledAPIs(json.data.enabled_apis || []);
                } else {
                    setEnabledAPIs([]);
                }
            })
            .catch((error) => {
                console.error('Error fetching enabled APIs:', error);
                alert('Error fetching enabled APIs');
                setEnabledAPIs([]);
            });
    }

    useEffect(() => {
      getEnabledAPIs();
    }, []);

    return (
        <div className="index-page">
            <div className="container-fluid">
                {/* Header */}
                <div className="row justify-content-center">
                    <div className="col-lg-10 col-xl-8">
                        <div className="text-center mb-5">
                            <h1 className="title mb-3">Statement Extraction & Enrichment</h1>
                        </div>
                    </div>
                </div>

                {/* Main Form Card */}
                <div className="row justify-content-center">
                    <div className="col-lg-10 col-xl-8">
                        <div className="card shadow-sm mb-4">
                            <div className="card-body p-4">
                                {
                                enabledAPIs.length === 0 ? (
                                    <div className="text-center py-5">
                                        <div className="spinner-border text-secondary mb-3" role="status">
                                            <span className="visually-hidden">Loading...</span>
                                        </div>
                                        <div className="text-muted">Loading configuration...</div>
                                    </div>
                                ) : (
                                <Formik
                                    initialValues={{
                                        ...data,
                                        enrich: enabledAPIs.includes('enrich_transactions')
                                    }}
                                    enableReinitialize={true}
                                    validate={values => {
                                        const errors = {};
                                        if (!values.files || values.files.length === 0) {
                                            errors.files = 'At least one PDF file is required';
                                        } 
                                        else if (values.files.length > 30) {
                                            errors.files = 'Maximum 30 files allowed per request';
                                        } 
                                        else {
                                            // Check if all files are PDFs (validation should be based on processed files)
                                            const invalidFiles = values.files.filter(file => !file.file_name?.toLowerCase().endsWith('.pdf'));
                                            if (invalidFiles.length > 0) {
                                                errors.files = `Please select only PDF files! Invalid files: ${invalidFiles.map(f => f.file_name).join(', ')}`;
                                            }
                                        }
                                        return errors;
                                    }}
                                    onSubmit={(values, { setSubmitting }) => {
                                        handleSubmit(values, setSubmitting);
                                        setSubmitting(true);
                                    }}
                                >
                                    {({ values, isSubmitting, setFieldValue }) => (
                                        <Form>
                                            {/* PDF Upload */}
                                            <div className="row mb-4">
                                                <div className="col-md-3 d-flex align-items-start">
                                                    <label className="form-label fw-semibold text-start text-nowrap">
                                                        PDF Documents <span className="text-danger">*</span>
                                                    </label>
                                                </div>
                                                <div className="col-md-9">
                                                    <Field name='files'>
                                                        {({ form }) => {
                                                            const { setFieldValue, setFieldError } = form;
                                                            return (
                                                                <div>
                                                                    <input
                                                                        ref={fileInputRef}
                                                                        type="file"
                                                                        accept=".pdf"
                                                                        multiple={true}
                                                                        className="form-control"
                                                                        onChange={(e) => handleMultipleFiles(e, setFieldValue, setFieldError, 'files')}
                                                                    />
                                                                    <div className="mt-2 text-start">
                                                                        <small className="text-muted">
                                                                            Select up to 30 PDF files
                                                                        </small>
                                                                    </div>
                                                                </div>
                                                            );
                                                        }}
                                                    </Field>
                                                    <ErrorMessage 
                                                        className="text-danger small mt-1" 
                                                        name="files" 
                                                        component="div" 
                                                    />
                                                </div>
                                            </div>

                                            {/* Uploaded Files List */}
                                            {uploadedFiles.length > 0 && (
                                                <div className="row mb-4">
                                                    <div className="col-md-3 d-flex align-items-start">
                                                        <label className="form-label fw-semibold">Uploaded Files</label>
                                                    </div>
                                                    <div className="col-md-9">
                                                        <div className="border rounded p-3 bg-light">
                                                            {uploadedFiles.map((file, index) => (
                                                                <div key={index} className="d-flex align-items-center justify-content-between mb-2 p-2 bg-white rounded border">
                                                                    <div className="flex-grow-1 me-3">
                                                                        <div className="d-flex align-items-center mb-2">
                                                                            <i className="fas fa-file-pdf text-danger me-2"></i>
                                                                            <span className="fw-medium">{file.file_name}</span>
                                                                        </div>
                                                                        <div className="row">
                                                                            <div className="col-md-12">
                                                                                <input
                                                                                    type="password"
                                                                                    className="form-control form-control-sm"
                                                                                    placeholder="Leave blank if PDF is not password protected"
                                                                                    value={file.password}
                                                                                    onChange={(e) => updateFilePassword(index, e.target.value, setFieldValue, values, 'files')}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                    <div
                                                                        className="text-danger cursor-pointer p-1"
                                                                        style={{ cursor: 'pointer', fontSize: '18px' }}
                                                                        onClick={() => removeFile(index, setFieldValue, values)}
                                                                        title="Remove file"
                                                                    >
                                                                        ×
                                                                    </div>
                                                                </div>
                                                            ))}
                                                            <div className="text-center mt-2">
                                                                <small className="text-success">
                                                                    <i className="fas fa-check-circle me-1"></i>
                                                                    {uploadedFiles.length} file{uploadedFiles.length > 1 ? 's' : ''} uploaded successfully
                                                                </small>
                                                            </div>
                                                        </div>
                                                    </div>
                                                </div>
                                            )}

                                            {/* Options */}
                                            <div className="border-top pt-4">
                                                <h6 className="mb-3 text-muted">Processing Options</h6>
                                                
                                                {/* <div className="row mb-3">
                                                    <div className="col-md-3">
                                                        <label className="form-label fw-semibold">Store If Invalid</label>
                                                    </div>
                                                    <div className="col-md-9 d-flex align-items-center">
                                                        <Switch
                                                            checked={values.store_if_invalid}
                                                            onChange={() => setFieldValue("store_if_invalid", !values.store_if_invalid)}
                                                            onColor="#8fbc8f"
                                                            offColor="#e9ecef"
                                                            uncheckedIcon={false}
                                                            checkedIcon={false}
                                                            height={20}
                                                            width={44}
                                                        />
                                                        <small className="text-muted ms-3">Save statements that fail validation</small>
                                                    </div>
                                                </div>

                                                <div className="row mb-3">
                                                    <div className="col-md-3">
                                                        <label className="form-label fw-semibold">ISO Date Format</label>
                                                    </div>
                                                    <div className="col-md-9 d-flex align-items-center">
                                                        <Switch
                                                            checked={values.iso_dateformat}
                                                            onChange={() => setFieldValue("iso_dateformat", !values.iso_dateformat)}
                                                            onColor="#8fbc8f"
                                                            offColor="#e9ecef"
                                                            uncheckedIcon={false}
                                                            checkedIcon={false}
                                                            height={20}
                                                            width={44}
                                                        />
                                                        <small className="text-muted ms-3">Use YYYY-MM-DD date format</small>
                                                    </div>
                                                </div> */}

                                                <div className="row mb-4">
                                                    <div className="col-md-3 d-flex align-items-start">
                                                        <label className="form-label fw-semibold text-start">Data Enrichment</label>
                                                    </div>
                                                    <div className="col-md-9 d-flex align-items-center">
                                                        <Switch
                                                            disabled={!enabledAPIs.includes('enrich_transactions')}
                                                            checked={values.enrich}
                                                            onChange={() => setFieldValue("enrich", !values.enrich)}
                                                            onColor="#8fbc8f"
                                                            offColor="#e9ecef"
                                                            uncheckedIcon={false}
                                                            checkedIcon={false}
                                                            height={20}
                                                            width={44}
                                                        />
                                                        <small className="text-muted ms-3">
                                                            Categorize and enrich transactions
                                                        </small>
                                                    </div>
                                                </div>
                                            </div>

                                            {/* Submit & Clear Buttons */}
                                            <div className="text-center pt-3">
                                                <div className="d-flex justify-content-center gap-3">
                                                    <button 
                                                        className="btn btn-primary btn-lg px-5"
                                                        type="submit" 
                                                        disabled={
                                                            isSubmitting || !values.files || values.files.length === 0 ||
                                                            !enabledAPIs.includes('extract_statement_batch') ||
                                                            (output && Object.keys(output).length > 0) || error
                                                        }
                                                    >
                                                        {isSubmitting ? (
                                                            <>
                                                                <span className="spinner-border spinner-border-sm text-secondary me-2"></span>
                                                                Processing...
                                                            </>
                                                        ) : (
                                                            'Extract Data'
                                                        )}
                                                    </button>
                                                    <button 
                                                        className="btn btn-danger btn-lg px-5"
                                                        type="button"
                                                        disabled={
                                                            Object.keys(input).length === 0 &&
                                                            Object.keys(output).length === 0 &&
                                                            error === null &&
                                                            uploadedFiles.length === 0
                                                        }
                                                        onClick={() => {
                                                            if (fileInputRef.current) {
                                                                fileInputRef.current.value = '';
                                                            }
                                                            setFieldValue('files', []);
                                                            setUploadedFiles([]);
                                                            setInput({});
                                                            setOutput({});
                                                            setError(null);
                                                        }}
                                                    >
                                                        Clear
                                                    </button>
                                                </div>
                                            </div>
                                        </Form>
                                    )}
                                </Formik>
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                {/* Batch Results */}
                {output && Object.keys(output).length > 0 && <Batch data={output} />}

                {/* Error Display */}
                {error && (
                    <div className="row justify-content-center">
                        <div className="col-lg-10 col-xl-8">
                            <div className="card border-danger shadow-sm mb-4">
                                <div className="card-body">
                                    <div className="alert alert-danger mb-0" role="alert">
                                        <i className="fas fa-exclamation-triangle me-2"></i>
                                        <strong>Processing Error:</strong> {error}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default DataExtraction;